/**
 * Workflow v2 ActionPanel — right-rail revision-feedback portal + Approve CTA.
 *
 * AUMOVIO redesign: the surface reads as a FEEDBACK SUBMISSION PORTAL, not a
 * co-pilot chat. Users describe a change, submit it, and see a submission
 * history — the mental model is "file a revision request", never "talk to an
 * assistant". The flow is:
 * submit → `POST /workflow/{tid}/revise` → status `revising` → back to
 * `draft` with an acknowledgement entry.
 *
 * Submitting re-runs immediately — there is no confirm step. The portal
 * reskin removed the "ask a question" affordance that made accidental
 * re-runs possible, so the consequence is stated up front (standing caption
 * + form hint) rather than gated behind a dialog on every submission.
 *
 * The portal is always rendered — no entry card, and no collapse of its own.
 * Getting out of the way while the agent runs is the AppShell's job: it
 * unmounts this whole panel so the centre reclaims the width, driven by the
 * one subheader PanelToggle. A panel that hid its own contents would still
 * hold the column. When the step has no agent output yet (status ∈ {empty,
 * generating}) the form is disabled and a helper line replaces the history.
 *
 * Reads per-step UI state off the reducer:
 *   - `messages`, `draft` (top-level on the step slot)
 *   - `status` is read-only — drives enable/disable; backend owns it
 *
 * Persistence: the `chat` prop comes from `useChatPersistence`
 * (composed once in `WorkflowV2.tsx` and threaded down through the
 * AppShell). `chat.sendUser(text)` and `chat.recvAgent(text)`
 * dispatch the reducer write AND POST to backend, so every history
 * entry round-trips through `workflow_progress.chat_messages.{agent_name}`.
 *
 * Approve button — unchanged:
 *   - Hidden when `approveHidden` is set
 *   - Disabled in empty / generating / revising / failed / approved
 *
 * Revise wiring (unchanged): Submit → `useReviseFlow.onRevise`
 * → `POST /workflow/{tid}/revise`. The in-progress entry derives from
 * `status === 'revising' || revise.revising`. The revising→draft
 * transition fires one acknowledgement entry through `chat.recvAgent`.
 */
import React, { useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import { useReviseFlow } from '../hooks/useReviseFlow';
import { hasOutput, approveDisabledForStatus } from '../state/workflowV2Reducer';
import styles from './WorkflowV2ActionPanel.module.css';

export default function WorkflowV2ActionPanel({
  state,
  actions,
  onApprove,
  approving,
  onResume,
  resuming,
  approveHidden,
  threadId,
  refetchStatus,
  chat,
}) {
  const stepId = state.selectedStepId;
  const stepState = state.steps[stepId];

  // Story H6 — defensive guard. Reducer always seeds every catalog
  // step on init, so this is reachable only if the selected stepId
  // somehow points off the catalog (URL-injection edge case).
  if (!stepState) return null;

  const status = stepState.status;
  const isApproved = status === 'approved';
  const isFailed = status === 'failed';
  const approveDisabled = approveDisabledForStatus(status) || approving;
  // Failed is a recoverable state: the portal stays live so the user can
  // submit feedback (the agent re-runs with their guidance), and the
  // footer swaps the inert Approve for a Resume CTA.
  const portalDimmed = (approveDisabled && !isFailed) || resuming;

  const revise = useReviseFlow({ stepId, threadId, refetchStatus });

  return (
    <aside className={styles.panel}>
      <div className={styles.body}>
        <FeedbackPortal
          stepId={stepId}
          stepState={stepState}
          status={status}
          actions={actions}
          revise={revise}
          chat={chat}
          dimmed={portalDimmed}
          isFailed={isFailed}
        />
      </div>

      {!approveHidden && (
        <footer className={styles.approveFooter}>
          {isFailed ? (
            <Button
              variant="default"
              className={styles.approveBtn}
              startIcon={<Icon name="refresh" size={14} />}
              disabled={resuming}
              onClick={() => onResume?.()}
            >
              {resuming ? 'Resuming…' : 'Resume analysis'}
            </Button>
          ) : (
            <Button
              variant="default"
              className={styles.approveBtn}
              startIcon={!isApproved ? <Icon name="check" size={14} /> : null}
              disabled={approveDisabled}
              onClick={() => onApprove?.()}
            >
              {approving ? 'Approving…' : isApproved ? 'Approved' : 'Approve & Proceed'}
            </Button>
          )}
        </footer>
      )}
    </aside>
  );
}

function FeedbackPortal({ stepId, stepState, status, actions, revise, chat, dimmed, isFailed }) {
  const { messages, draft } = stepState;
  const historyRef = useRef(null);
  const textareaRef = useRef(null);

  // `dimmed` mirrors the Approve button's disabled state — empty /
  // generating / revising / failed / approved / mid-approve all dim
  // the portal surface the same way. Form interactions follow the
  // same flag. `noOutputYet` keeps the empty-state placeholder text
  // narrowly scoped to the pre-draft states (otherwise approved /
  // failed steps would show "waiting for draft" misleadingly).
  const noOutputYet = !hasOutput(status);
  const formDisabled = dimmed;
  const isReviseInFlight = revise.revising || status === 'revising';

  // Auto-scroll history to bottom on new entry / progress transitions.
  useEffect(() => {
    const el = historyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, isReviseInFlight]);

  // When status transitions from `revising` → `draft|approved`, append
  // a one-shot acknowledgement so the history reflects the round-trip.
  // Routes through `chat.recvAgent` so the entry also POSTs to
  // backend. Ref-guarded edge detection so we only fire on the
  // transition, not on every render where status === 'draft'.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (prev === 'revising' && (status === 'draft' || status === 'approved')) {
      const ack = 'Feedback applied — draft updated. Review the changes in the table below.';
      if (chat?.recvAgent) {
        chat.recvAgent(ack);
      } else {
        // Defensive fallback if the persistence layer is missing
        // (e.g. tests). Keep the local entry at least.
        actions.receiveAgentReply(stepId, ack);
      }
    }
    prevStatusRef.current = status;
  }, [status, stepId, actions, chat]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [draft]);

  const onChange = (e) => actions.updateDraft(stepId, e.target.value);

  const onSubmit = async () => {
    const text = (draft || '').trim();
    if (!text || isReviseInFlight || formDisabled) return;

    // Submitting RE-RUNS the step's agent, which regenerates its output —
    // ratings included. That consequence is stated standing in the caption and
    // the form hint rather than gated behind a per-submission dialog: the
    // portal reskin removed the chat affordance that made people submit
    // questions by accident, so the confirm was asking about an action the
    // surface no longer invites.

    // 1. Dispatch + persist the submission.
    if (chat?.sendUser) {
      chat.sendUser(text); // intentionally not awaited; UX is optimistic
    } else {
      actions.sendUserMessage(stepId);
    }

    // 2. Fire the revise call.
    try {
      await revise.onRevise(text);
    } catch (e) {
      // useReviseFlow already fired a toast + stored .error; surface
      // an inline entry so the user sees feedback in the history.
      const errText = `Feedback could not be applied: ${revise.error || e?.message || 'unknown error'}. Try again.`;
      if (chat?.recvAgent) {
        chat.recvAgent(errText);
      } else {
        actions.receiveAgentReply(stepId, errText);
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className={`${styles.portal} ${dimmed ? styles.portalDimmed : ''}`}>
      <header className={styles.portalHead}>
        <div className={styles.portalHeadRow}>
          <span className={styles.actionIcon}><Icon name="edit" size={14} /></span>
          <span className={styles.portalTitle}>Revision Feedback</span>
        </div>
        <p className={styles.portalCaption}>
          Submitting re-runs this step; its results, including ratings, may change.
        </p>
      </header>

      <div className={styles.history} ref={historyRef}>
        {messages.length === 0 ? (
          <div className={styles.historyEmpty}>
            {isFailed
              ? 'The agent run failed. Describe what to change and submit, or use Resume analysis below.'
              : noOutputYet
              ? 'Feedback opens once the agent produces a draft.'
              : 'No feedback submitted for this step yet.'}
          </div>
        ) : (
          <>
            {messages.map((m) => <HistoryEntry key={m.id} message={m} />)}
            {isReviseInFlight && <ProgressEntry />}
          </>
        )}
      </div>

      <div className={styles.form}>
        <textarea
          ref={textareaRef}
          className={styles.formInput}
          placeholder={
            isFailed
              ? 'Describe what to change; submitting re-runs the agent…'
              : noOutputYet
              ? 'Feedback opens once the agent produces a draft.'
              : 'Describe the changes to apply to this step’s results…'
          }
          value={draft}
          rows={2}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={formDisabled}
        />
        <Button
          variant="default"
          size="sm"
          className={styles.submitBtn}
          onClick={onSubmit}
          disabled={formDisabled || !draft.trim() || isReviseInFlight}
          title="Submit feedback (Enter)"
          aria-label="Submit feedback"
        >
          {isReviseInFlight ? 'Applying feedback…' : 'Submit feedback'}
        </Button>
        <p className={styles.formHint}>
          Press Enter to submit, Shift + Enter for a new line.
        </p>
      </div>
    </div>
  );
}

function HistoryEntry({ message }) {
  // User submissions render as filed-request cards; agent/system entries
  // (the backend welcome injection and revise acknowledgements) render as
  // muted status notes — nothing about the surface says "conversation".
  const isUser = message.role === 'user';
  const text = String(message.text || '').split('\n').map((line, i) => (
    <span key={i} className={styles.entryLine}>{line || ' '}</span>
  ));

  if (isUser) {
    return (
      <div className={styles.submission}>
        <span className={styles.submissionLabel}>Feedback submitted</span>
        <div className={styles.entryText}>{text}</div>
      </div>
    );
  }
  return (
    <div className={styles.statusNote}>
      <span className={styles.statusNoteIcon}><Icon name="info" size={13} /></span>
      <div className={styles.entryText}>{text}</div>
    </div>
  );
}

function ProgressEntry() {
  return (
    <div className={`${styles.statusNote} ${styles.progressNote}`}>
      <span className={styles.progressDot} />
      <span className={styles.progressDot} />
      <span className={styles.progressDot} />
      <span className={styles.progressLabel}>Applying feedback — revision in progress…</span>
    </div>
  );
}
